import Box from '@mui/material/Box';
import type {
  GridReadyEvent,
  RowClickedEvent,
  StateUpdatedEvent
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';

import LoadingIndicator from '@/components/LoadingIndicator';
import useGridTheme from '@/hooks/useGridTheme';
import { ALERT_RULES_TABLE_DEFAULT_SORT_MODEL } from '@/pages/AlertRules/AlertRules.constants';
import {
  alertRulesGridApiAtom,
  filteredAlertRulesAtom,
  selectedAlertRuleAtom,
  useAlertRuleSelection
} from '@/pages/AlertRules/AlertRules.store';
import { selectedOrganizationIdAtom } from '@/pages/Organizations';
import {
  gridGetStoredState,
  gridSetPage,
  gridSetStoreState
} from '@/utils/Grid.utils';

import {
  ALERT_RULES_COLUMN_DEFS,
  ALERT_RULES_GRID_OPTIONS
} from './AlertRulesTable.constants';

const AlertRulesTable = ({ isLoading }: { isLoading: boolean }) => {
  const [alertRulesGridApi, setAlertRulesGridApi] = useAtom(
    alertRulesGridApiAtom
  );

  const [filteredAlertRules] = useAtom(filteredAlertRulesAtom);
  const [selectedOrganizationId] = useAtom(selectedOrganizationIdAtom);
  const [selectedAlertRule] = useAtom(selectedAlertRuleAtom);

  const { selectAlertRule } = useAlertRuleSelection();

  const gridTheme = useGridTheme();

  const handleGridReady = useCallback((params: GridReadyEvent) => {
    setAlertRulesGridApi(params.api);
  }, []);

  const handleStateUpdate = useCallback(
    (event: StateUpdatedEvent) => gridSetStoreState(event, 'AlertRulesTable'),
    []
  );

  const handleRowClick = useCallback(
    (event: RowClickedEvent) => {
      selectAlertRule(event.data.trigger_id);
    },
    [selectAlertRule]
  );

  useEffect(() => {
    // on selected alert rule change - select row and navigate to its page
    if (
      alertRulesGridApi &&
      selectedAlertRule &&
      !alertRulesGridApi.isDestroyed()
    ) {
      const selectedRows = alertRulesGridApi.getSelectedRows();
      if (
        selectedRows?.[0]?.id === parseInt(selectedAlertRule.id.toString(), 10)
      ) {
        return;
      }
      alertRulesGridApi.forEachNode((node) => {
        if (node.data.id === parseInt(selectedAlertRule.id.toString(), 10)) {
          node.setSelected(true, true);
          const pageSize = alertRulesGridApi.paginationGetPageSize();
          if (node.rowIndex == null) {
            return;
          }
          const page = Math.floor(node.rowIndex / pageSize);
          alertRulesGridApi.paginationGoToPage(page);
        }
      });
    }
  }, [alertRulesGridApi, filteredAlertRules, selectedAlertRule]);

  // Reset to first page when list of cases changes or organization changes
  useEffect(() => {
    if (alertRulesGridApi && !alertRulesGridApi.isDestroyed()) {
      alertRulesGridApi.paginationGoToPage(0);
    }
  }, [selectedOrganizationId]);

  const gridOptions = useMemo(() => {
    // restore sorting and pagination state on mount
    const initialState = gridGetStoredState(
      'AlertRulesTable',
      ALERT_RULES_TABLE_DEFAULT_SORT_MODEL
    );
    return {
      ...ALERT_RULES_GRID_OPTIONS,
      initialState
    };
  }, []);

  return (
    <Box
      className="ag-theme-alpine rounded-4px"
      sx={{ border: 'none !important' }}
    >
      <LoadingIndicator isLoading={isLoading} />
      <AgGridReact
        columnDefs={ALERT_RULES_COLUMN_DEFS}
        gridOptions={gridOptions}
        rowData={filteredAlertRules}
        theme={gridTheme}
        onGridReady={handleGridReady}
        onFirstDataRendered={(params) =>
          gridSetPage(params.api, 'AlertRulesTableState')
        }
        onRowClicked={handleRowClick}
        onStateUpdated={handleStateUpdate}
      />
    </Box>
  );
};

export default AlertRulesTable;
